// Copyright (C) 2026 FlexiProof. All rights reserved.
//
// This source code file is a part of free software licensed under the terms of
// the MIT License as published by the Massachusetts Institute of Technology:
// you can use, copy, modify and distribute any part of it without limitation,
// subject to the conditions contained within that license.
//
// This source code file, and the software it forms a part of, IS PROVIDED "AS
// IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED. See the MIT License
// for more details.
//
// You should have received a copy of the MIT License along with this source
// code file in the root of this repository. If not, see one of the following
// 3rd party sites for a copy of the license template:
// - <https://opensource.org/licenses/MIT>
// - <https://choosealicense.com/licenses/mit>
// - <https://spdx.org/licenses/MIT>

/**
 * @file Web component for rendering deck to canvas using Pixi.
 * @author James Reid
 */

// @ts-check

// @@imports-dependencies
import { Application, Container, Graphics, Point, Sprite, Text } from "pixi.js"
import {
    metricToImperial,
    Vector2D,
    Edge,
    Deck,
    Apex
} from "../../../../../package/index.js"

// @@imports-package
import { App } from "../../../app/App.js"
import { hslToRgb } from "../../../utils/index.js"
import { EdgeEditor } from "./EdgeEditor.js"
import { SteppedContainer } from "../../containers/SteppedContainer.js"
import { ApexHeightEditor } from "./ApexHeightEditor.js"

// @@body
class Renderer extends HTMLElement {
    app = new Application()
    renderTargets = /** @type {{container:Container, render:any}[]} */ ([])
    selectedEdge = /** @type {Edge?} */ (null)
    scales = {
        app: 1,
        client: 1,
        fixed: 0.7,
        get combined() { return this.app * this.client * this.fixed }
    }
    offsets = {
        app: new Vector2D(0, 0),
        client: new Vector2D(0, 0),
        fixed: new Vector2D(0, 0),
        get combined() { return this.app.add(this.client).add(this.fixed) }
    }
    canvasHeightCorrections = {
        app: 0,
        client: 0,
        fixed: 0,
        get combined() { return this.app + this.client + this.fixed }
    }
    renderFeatures = {
        deck: true,
        edges: true,
        measurements: true,
        types: false
    }
    sizes = {
        edgeStrokeWidth: 2,
        selectedEdgeStrokeWidth: 16,
        measurementFont: 16,
        measurementTextOffset: 4
    }
    colors = {
        deckFill: 0xbfbfbf,
        edgeStroke: 0x212121,
        selectedEdgeStroke: 0x0e3553,
        measurementStroke: 0x000000,
        measurementFont: 0x000000
    }
    flags = {
        metric: true
    }
    #animating = false
    #mobileTouchY = 0
    #mobileTouchStartListener = (/** @type {TouchEvent} */ event) => {
        const touches = event.touches
        if (touches.length === 1) {
            const { screenY } = touches[0]
            this.#mobileTouchY = screenY
        }
    }
    #mobileTouchMoveListener = (/** @type {TouchEvent} */ event) => {
        const touches = event.touches
        if (touches.length === 1) {
            const { screenY } = touches[0]
            const message = {
                action: "MOBILE_EMULATE_SCROLL",
                top: this.#mobileTouchY - screenY
            }
            window.parent.postMessage(message, "*")
        }
    }

    constructor() {
        super()
    }

    connectedCallback() {
        const { height: bottomNavHeight } = /** @type {HTMLElement} */ (document
            .querySelector("nav.bottom"))
            .getBoundingClientRect()

        const fixedOffset = - bottomNavHeight / 2
        this.offsets.fixed = new Vector2D(0, fixedOffset)
        this.canvasHeightCorrections.fixed = - bottomNavHeight

        this.append(this.app.canvas)
        this.start()

        this.app.canvas.addEventListener(
            "touchstart",
            this.#mobileTouchStartListener
        )
        this.app.canvas.addEventListener(
            "touchmove",
            this.#mobileTouchMoveListener
        )
    }

    disconnectedCallback() {
        this.app.canvas.removeEventListener(
            "touchstart",
            this.#mobileTouchStartListener
        )
        this.app.canvas.removeEventListener(
            "touchmove",
            this.#mobileTouchMoveListener
        )
    }

    async init() {
        const { width, height } = /** @type {HTMLElement} */ (document
            .querySelector("stepped-container"))
            .getBoundingClientRect()
        const { height: topNavHeight } = /** @type {HTMLElement} */ (document
            .querySelector("nav.top"))
            .getBoundingClientRect()

        await this.app.init({
            width,
            height: height - topNavHeight,
            resolution: window.devicePixelRatio,
            autoDensity: true,
            backgroundAlpha: 0
        })
    }

    start() {
        this.render({ reset: true })
        this.#animating = true
        this.#animate()
    }

    stop() {
        this.#animating = false
    }

    render({ reset = false } = {}) {}

    tween(callback, {
        start = 0,
        end = 1,
        duration = 600,
        tweenStart = Date.now()
    } = {}) {
        const now = Date.now()

        if (now >= tweenStart + duration) { callback(end) }
        else {
            window.requestAnimationFrame(() => {
                const timeProgress = (now - tweenStart) / duration
                const tweenProgress = timeProgress * (2 - timeProgress)
                callback(start + (end - start) * tweenProgress)

                this.tween(callback, { start, end, duration, tweenStart })
            })
        }
    }

    #animate() {
        if (this.#animating) {
            this.render()
            window.requestAnimationFrame(() => this.#animate())
        }
    }

    get container() {
        return App.instance.container
    }

    get spritesheet() {
        return App.instance.state.spritesheet
    }

    get deck() {
        return /** @type {Deck} */ (App.instance.state.deck.client)
    }
}

class DeckRenderer extends Renderer {
    constructor() {
        super()
    }

    render({ reset = false } = {}) {
        if (reset) {
            const container = new Container()
            this.renderTargets = []

            if (this.renderFeatures.deck) {
                const deckTarget = this.#getDeckTarget()
                container.addChild(deckTarget.container)
                this.renderTargets.push(deckTarget)
            }

            if (this.renderFeatures.edges) {
                for (const { value: edge } of this.deck.edges) {
                    const edgeTarget = this.#getEdgeTarget(edge)
                    container.addChild(edgeTarget.container)
                    this.renderTargets.push(edgeTarget)
                }
            }

            if (this.renderFeatures.measurements) {
                for (const { value: edge } of this.deck.edges) {
                    const measurementTarget = this.#getMeasurementTarget(edge)
                    container.addChild(measurementTarget.container)
                    this.renderTargets.push(measurementTarget)
                }
            }

            if (this.renderFeatures.types) {
                for (const { value: edge } of this.deck.edges) {
                    const typeTarget = this.#getTypeTarget(edge)
                    container.addChild(typeTarget.container)
                    this.renderTargets.push(typeTarget)
                }
            }

            for (const apex of this.deck.apexes) {
                const apexTarget = this.#getApexTarget(apex)
                container.addChild(apexTarget.container)
                this.renderTargets.push(apexTarget)
            }

            for (const child of this.app.stage.children) {
                this.app.stage.removeChild(child)
            }
            this.app.stage.addChild(container)
        }

        // Update global offset of root Pixi container in order to keep the
        // rendered deck/content centred on the screen. Note that the scale of
        // the root Pixi container is *not* updated here such that scaling
        // issues with elements such as text (which should always be a constant
        // size) are avoided.
        const { x, y } = this.offset
        this.app.stage.children[0].x = x
        this.app.stage.children[0].y = y

        for (const { render } of this.renderTargets) { render() }
    }

    #getDeckTarget() {
        const container = new Container()
        const graphic = new Graphics()
        container.addChild(graphic)
    
        const render = () => {
            const vertices = this.deck.vertices.array.map(vertex => {
                return new Point(vertex.x * this.scale, vertex.y * this.scale)
            })

            graphic.clear()
                .poly(vertices)
                .fill(this.colors.deckFill)
        }
    
        return { container, render }
    }

    #getEdgeTarget(edge) {
        const container = new Container()
        const graphic = new Graphics()
        container.addChild(graphic)

        const render = ({ width = 2 } = {}) => {
            const easeFactor = (1 + Math.sin((Date.now() * 2 * Math.PI / 1200))) / 2
            const {
                edgeHue,
                edgeSaturation,
                edgeLightness,
                edgeWidth
            } = this.selectedEdge === edge
                ? {
                    // edgeHue: 206,
                    edgeHue: 0,
                    // edgeSaturation: 0 + 71 * easeFactor,
                    edgeSaturation: 0,
                    // edgeLightness: 0 + 19 * easeFactor,
                    edgeLightness: 0 + 13 * easeFactor,
                    edgeWidth: 2 + 12 * easeFactor
                }
                : {
                    edgeHue: 0,
                    edgeSaturation: 0,
                    edgeLightness: 13,
                    edgeWidth: width
                }
            container.zIndex = this.selectedEdge === edge ? 2 : 1
            graphic.clear()
                .moveTo(edge.start.x * this.scale, edge.start.y * this.scale)
                .lineTo(edge.end.x * this.scale, edge.end.y * this.scale)
                .stroke({
                    color: hslToRgb(edgeHue, edgeSaturation, edgeLightness).hex,
                    width: edgeWidth,
                    cap: "square"
                })
        }

        return { container, render }
    }

    #getMeasurementTarget(edge, {
        edgeOffset = 16,
        endOffset = 16,
        crossBarWidth = 16
    } = {}) {
        const container = new Container()
        const line = new Graphics()
        const crossBars = new Graphics()
        const text = new Text({ style: {
            fontSize: this.sizes.measurementFont,
            fill: this.colors.measurementFont,
            // fontWeight: "lighter"
            fontWeight: "bold"
        } })
        container.addChild(line, crossBars, text)

        const strokeStyle = {
            color: this.colors.measurementStroke,
            width: this.sizes.edgeStrokeWidth
        }

        // 
        const render = () => {
            const { vertexOrientation: orientation } = this.deck
            const offsetWidthRatio = edgeOffset / this.sizes.edgeStrokeWidth
            const edgeAlignment = orientation * (offsetWidthRatio + 0.5)
            const measurementLength = edge.magnitude * this.scale

            line.clear()
                .moveTo(endOffset, 0)
                .lineTo(measurementLength - endOffset, 0)
                .stroke({ ...strokeStyle, alignment: edgeAlignment })

            const barAlignment = orientation * edgeOffset
            const crossBarStart = crossBarWidth / 2 - barAlignment
            const crossBarEnd = - (barAlignment + crossBarWidth / 2)
            crossBars.clear()
                .moveTo(endOffset, crossBarStart)
                .lineTo(endOffset, crossBarEnd)
                .stroke(strokeStyle)
                .moveTo(measurementLength - endOffset, crossBarStart)
                .lineTo(measurementLength - endOffset, crossBarEnd)
                .stroke(strokeStyle)

            // text.style.fontWeight = this.deck.isSetEdgeLength(edge) ? "bold" : "lighter"
            if (this.flags.metric) {
                text.text = `${(edge.magnitude / 100).toFixed(2)}m`
            }
            else {
                const { feet, inches } = metricToImperial(edge.magnitude / 100)
                text.text = `${Math.round(feet)}' ${Math.round(inches)}"`
            }

            container.rotation = edge.direction
            container.x = edge.start.x * this.scale
            container.y = edge.start.y * this.scale

            text.x = endOffset + this.sizes.measurementTextOffset
            text.y = orientation === 1
                ? - (edgeOffset + text.height)
                : edgeOffset

            if (
                orientation === 1 && container.rotation === Math.PI ||
                orientation === - 1 && container.rotation !== 0
            ) {
                text.rotation = Math.PI
                text.x += text.width
                text.y += text.height
            }
        }

        // Make edge length text interactive if edge type selector is *not*
        // active. Edge type editor check prevents users from escaping deck
        // specification flow then not being able to continue due to the next
        // step button remaining inactive.
        const edgeTypeEditor = document.querySelector("edge-type-editor")
        if (!edgeTypeEditor) {
            text.eventMode = "static"
            text.cursor = "pointer"
            text.on("pointerup", () => {
                const existingEdgeEditor =
                    document.querySelector("edge-editor") ||
                    document.querySelector("edge-length-editor")

                if (existingEdgeEditor) {
                    if (this.selectedEdge === edge) {
                        const lengthInput = existingEdgeEditor
                            .querySelector("div.unit:not(.closed) input")
                        lengthInput.select()
                    }
                    else {
                        existingEdgeEditor.edge = edge
                        const lengthInput = existingEdgeEditor
                            .querySelector("div.unit:not(.closed) input")
                        lengthInput.select()
                    }
                }
                else {
                    const popoverContainer = SteppedContainer.popoverContainer()

                    this.selectedEdge = edge
                    const edgeEditor = new EdgeEditor()

                    popoverContainer.append(edgeEditor)
                    this.container.loadPopover(popoverContainer)
                }
                // render()
                // state.deckEditor.render() // required due to color change of other edge

                // move into draw func
            })
        }

        return { container, render }
    }

    #getTypeTarget(edge) {
        const container = new Container()

        const t = new Graphics()
        const sprite = new Sprite(this.spritesheet.none)
        const edgeType = new Graphics()
        const edgeTypeBorder = new Graphics()
        const edgeTypeText = new Text({
            text: "Click to select",
            style: { fontSize: 16 }
        })
        const edgeTypeTextBorder = new Graphics()
        const numberBorder = new Graphics()
        const number = new Text({
            text: this.deck?.edges.indexOfValue(edge) + 1,
            style: { fontSize: 16 }
        })

        // typeSprite, typeBorder, edgeNumber, numberBorder


        container.addChild(sprite, edgeType, edgeTypeBorder, numberBorder, number, edgeTypeText, edgeTypeTextBorder, t)

        // container.addChild(graphic, measurement, sprite, t)
        // container.addChild(graphic, measurement, sprite, edgeType, edgeTypeBorder, numberBorder, number, t)

        // const aspectRatio = this.deck.boundingBox.width / this.deck.boundingBox.height
        const originalWidth = sprite.width
        // const targetImageWidth = Math.min(
        //     this.width.renderer * 0.2,
        //     edge.magnitude * this.scale * 0.375 * aspectRatio
        // )
        // // const targetImageWidth = this.width.renderer * 0.08
        // sprite.scale = 1 * (targetImageWidth / originalWidth)
        // sprite.zIndex = 2

        // container.zIndex = 3

        const render = () => {
            const aspectRatio = this.deck.boundingBox.width / this.deck.boundingBox.height
            const targetImageWidth = Math.min(
                this.width.renderer * 0.2,
                edge.magnitude * this.scale * 0.375
            )
            // const targetImageWidth = this.width.renderer * 0.08
            sprite.scale = 1 * (targetImageWidth / originalWidth)
            sprite.zIndex = 2

            container.zIndex = 3

            number.style = {
                fontSize: Math.max(Math.min(16, edge.magnitude * this.scale * 0.078125), 8),
                fill: this.selectedEdge === edge ? 0xffffff : 0x000000
            }
            edgeTypeText.style = {
                fontSize: Math.max(Math.min(16, edge.magnitude * this.scale * 0.078125), 8),
                fill: this.selectedEdge === edge ? 0xffffff : 0x000000
            }

            const midPoint = edge.start.add(edge.scalarMultiply(0.5))
            edgeType
                .clear()
                .roundRect(
                    midPoint.x * this.scale - sprite.width / 2,
                    midPoint.y * this.scale - sprite.height / 2,
                    sprite.width,
                    sprite.height,
                    10
                )
                .fill(0x212121)

            edgeTypeBorder
                .clear()
                .roundRect(
                    midPoint.x * this.scale - sprite.width / 2,
                    midPoint.y * this.scale - sprite.height / 2,
                    sprite.width,
                    sprite.height,
                    10
                )
                .stroke({
                    // color: 0x0e3553,
                    color: 0x000000,
                    width: 3
                })

            edgeTypeBorder.zIndex = 2

            numberBorder
                .clear()
                .circle(
                    midPoint.x * this.scale,
                    midPoint.y * this.scale + sprite.height / 2,
                    number.width * 1.25
                )
                .stroke({
                    // color: 0x0e3553,
                    color: 0x000000,
                    width: 5
                })
                .fill(this.selectedEdge === edge ? 0x000000 : 0xffffff)

            let eT = this.deck.getEdgeType(edge)

            numberBorder.zIndex = 2
            number.zIndex = 3

            number.x = midPoint.x * this.scale - number.width / 2
            number.y = midPoint.y * this.scale + sprite.height / 2 - number.height / 2

            edgeTypeTextBorder.zIndex = 2
            edgeTypeText.zIndex = 3
            if (this.deck.isSetEdgeType(edge)) {
                const leadingCharacter = eT[0].toUpperCase()
                edgeTypeText.text = `${leadingCharacter}${eT
                    .slice(1)
                    .split(/(?=[A-Z])/)
                    .join(" ")}`
            }
            edgeTypeText.x = midPoint.x * this.scale - edgeTypeText.width / 2
            edgeTypeText.y = midPoint.y * this.scale - sprite.height / 2 - edgeTypeText.height / 2

            edgeTypeTextBorder
                .clear()
                .roundRect(
                    midPoint.x * this.scale - edgeTypeText.width * 1.25 / 2,
                    midPoint.y * this.scale - edgeTypeText.height * 1.25 / 2 - sprite.height / 2,
                    edgeTypeText.width * 1.25,
                    edgeTypeText.height * 1.25
                )
                .stroke({
                    color: 0x000000,
                    width: 5
                })
                .fill(this.selectedEdge === edge ? 0x000000 : 0xffffff)

            sprite.x = midPoint.x * this.scale - sprite.width / 2
            sprite.y = midPoint.y * this.scale - sprite.height / 2
            sprite.mask = edgeType

            // if (eT === EDGE_TYPES.parapetWall) { eT = EDGE_TYPES.houseWall }
            // if (eT === EDGE_TYPES.buttJoin || eT === EDGE_TYPES.seamJoin) {
            //     eT = "rubberJoin"
            // }

            if (this.deck.isSetEdgeType(edge)) {
                sprite.texture = this.spritesheet[eT]
            }
            else { sprite.texture = this.spritesheet["noJoinSelected"] }
            const { x, y } = sprite.getBounds()
            // sprite.hitArea

            const he = (edge.magnitude / 2) * this.scale
            const hi = edge.direction % Math.PI
                ? sprite.height / 2
                : sprite.width / 2
            t
                .clear()
                .rotateTransform(edge.direction)
                .translateTransform(edge.start.x * this.scale, edge.start.y * this.scale)
                .rect(0, - 25, he - hi, 50) // determine width based on sprite width... 
                .rect(he + hi, - 25, he - hi, 50)
                .fill({
                    color: 0x000000,
                    alpha: 0
                })
        }

        // render() // for initial hit area ... remove with fallback instead 

        let dragging = false
        let dragStart = { x: NaN, y: NaN }
        let selectedEdge = null
        let selectingEdge = false

        sprite.eventMode = "static"
        sprite.cursor = "pointer"
        sprite.on("pointerdown", () => {
            selectingEdge = true
        })
        sprite.on("pointerup", () => {
            if (!selectingEdge) { return }

            const existingEdgeEditor = document.querySelector("edge-editor") ||
                document.querySelector("edge-type-editor")
            // const existingEdgeEditor = App.container.getContainer("popover")
            if (existingEdgeEditor) {
                if (this.selectedEdge === edge) {
                    existingEdgeEditor.querySelector("list-select").open()
                }
                else {
                    existingEdgeEditor.edge = edge
                    existingEdgeEditor.querySelector("list-select").open()
                }
            }
            else {
                this.selectedEdge = edge
                const edgeEditor = new EdgeEditor()

                const popoverContainer = SteppedContainer.popoverContainer()
                popoverContainer.append(edgeEditor)
                App.container.loadPopover(popoverContainer)
            }

            selectingEdge = false
            // render()
            // state.deckEditor.render() // required due to color change of other edge

            // move into draw func
        })

        // // add debounce for taps on mobile ... 
        // // t.eventMode = "static"
        // // t.cursor = "grab"
        // t.on("pointerdown", event => {
        //     const edgeEditor = document.querySelector("edge-editor")
        //     if (edgeEditor) {
        //         if (document.body.classList.contains("hoverable")) {
        //             edgeEditor.remove()
        //         }
        //         return
        //     }

        //     state.deckEditor.dragging = true
        //     dragging = true
        //     t.cursor = "grabbing"
        //     const { x, y } = root.getBoundingClientRect()
        //     dragStart = { x: event.x - x, y: event.y - y }

        //     selectedEdge = edge

        //     // fix this render call (not working due to re-render on resize) 
        //     // render({ width: 5 })

        //     // document.querySelector("edge-editor")?.classList.add("closed")
        //     // document.querySelector("edge-editor")?.remove()
        // })
        // t.on("pointermove", event => {
        //     if (!dragging) { return }

        //     // add this rescale as an option which is disabled when client pans or 
        //     // zooms, then can be re-enabled... 
        //     const { deckEditor: { scale } } = state

        //     // move into draw func
        //     render({ width: 5 })


        //     const { x, y } = root.getBoundingClientRect()
        //     const drag = { x: event.x - x - state.deckEditor.anchor.x, y: event.y - y - state.deckEditor.anchor.y }

        //     const edgeCount = state.deckEditor.deck?.edges.size
        //     try {
        //         state.deckOption.deck.moveEdge(
        //             edge,
        //             edge.direction % Math.PI
        //                 ? new Vector2D(Math.round(drag.x / scale), edge.start.y)
        //                 : new Vector2D(edge.start.x, Math.round(drag.y / scale)),
        //             { dissolveVertices: false }
        //         )
        //     }
        //     catch {}

        //     dragStart = drag
        //     // if (edgeCount !== state.deckEditor.deck?.edges.size) {
        //     //     state.deckEditor.rerender()
        //     // }
        //     // else { state.deckEditor.render() }

        //     state.deckEditor.render()
        // })
        // t.on("pointerleave", () => {
        //     if (state.deckEditor.dragging) {
        //         // MOVE TO EXTERNAL FUNCTION SINCE DUPLICATED BELOW... 
        //         state.deckOption?.deck.dissolveVertices()

        //         const {
        //             deckEditor: { scale, targetScale, anchor, targetAnchor }
        //         } = state
        //         const toTargetScale = targetScale - scale
        //         const toTargetAnchor = targetAnchor.subtract(anchor)
        //         tweenDeckEditor(tweenProgress => {
        //             state.deckEditor.anchor = anchor
        //                 .add(toTargetAnchor.scalarMultiply(tweenProgress))
        //             state.deckEditor.scale = scale + toTargetScale * tweenProgress
        //         }, state)
        //     }

        //     state.deckEditor.dragging = false
        //     dragging = false
        //     t.cursor = "grab"
        //     selectedEdge = null

        //     render()
        // })
        // t.on("pointerup", () => {
        //     if (state.deckEditor.dragging) {
        //         // MOVE TO EXTERNAL FUNCTION SINCE DUPLICATED BELOW... 
        //         state.deckOption?.deck.dissolveVertices()

        //         const {
        //             deckEditor: { scale, targetScale, anchor, targetAnchor }
        //         } = state
        //         const toTargetScale = targetScale - scale
        //         const toTargetAnchor = targetAnchor.subtract(anchor)
        //         tweenDeckEditor(tweenProgress => {
        //             state.deckEditor.anchor = anchor
        //                 .add(toTargetAnchor.scalarMultiply(tweenProgress))
        //             state.deckEditor.scale = scale + toTargetScale * tweenProgress
        //         }, state)
        //     }

        //     state.deckEditor.dragging = false
        //     dragging = false
        //     t.cursor = "grab"
        //     selectedEdge = null

        //     render()
        // })

        return { container, render }
    }

    #getApexTarget(apex) {
        const container = new Container()
        const graphic = new Graphics()
        container.addChild(graphic)

        const render = ({ width = 1 } = {}) => {
            const easeFactor = (1 + Math.sin((Date.now() * 2 * Math.PI / 1200))) / 2
            const {
                edgeHue,
                edgeSaturation,
                edgeLightness,
                edgeWidth
            } = {
                edgeHue: 0,
                edgeSaturation: 0,
                edgeLightness: 50,
                edgeWidth: width
            }
            // container.zIndex = this.selectedEdge === edge ? 2 : 1
            const intercept = this.deck.apexGradient === 0
                ? "yIntercept" : "xIntercept"
            graphic.clear()
                .moveTo(apex[intercept] * this.scale, 0)
                .lineTo(apex[intercept] * this.scale, this.height.deck * this.scale)
                .stroke({
                    color: hslToRgb(edgeHue, edgeSaturation, edgeLightness).hex,
                    width: edgeWidth,
                    cap: "round"
                })
        }

        return { container, render }
    }

    get width() {
        return {
            deck: this.deck.boundingBox.width,
            renderer: this.app.renderer.width
        }
    }

    get height() {
        return {
            deck: this.deck.boundingBox.height,
            renderer: this.app.renderer.height
        }
    }

    get offset() {
        const { deck: deckWidth, renderer: rendererWidth } = this.width
        const { deck: deckHeight, renderer: rendererHeight } = this.height

        const anchorX = (rendererWidth - deckWidth * this.scale) / 2
        const anchorY = (rendererHeight - deckHeight * this.scale) / 2

        return new Vector2D(anchorX, anchorY)
            .add(this.offsets.combined)
    }

    get scale() {
        const { deck: deckWidth, renderer: rendererWidth } = this.width
        const { deck: deckHeight, renderer: rendererHeight } = this.height

        const scale = Math.min(
            rendererWidth / deckWidth,
            (rendererHeight + this.canvasHeightCorrections.combined) / deckHeight
        )

        return scale * this.scales.combined
    }

    static get customName() {
        return "deck-renderer"
    }
}

class ApexRenderer extends Renderer {
    selectedApex = /** @type {Apex?} */ (null)

    constructor() {
        super()
    }

    render({ reset = false } = {}) {
        if (reset) {
            const container = new Container()
            this.renderTargets = []

            // if (this.renderFeatures.deck) {
                const apexCrossSectionTarget = this.#getApexCrossSectionTarget()
                container.addChild(apexCrossSectionTarget.container)
                this.renderTargets.push(apexCrossSectionTarget)
            // }

            const eavesMeasurementTarget = this.#getEavesMeasurementTarget()
            container.addChild(eavesMeasurementTarget.container)
            this.renderTargets.push(eavesMeasurementTarget)

            for (const apex of this.deck.apexes) {
                const apexTarget = this.#getApexTarget(apex)
                container.addChild(apexTarget.container)
                this.renderTargets.push(apexTarget)
            }

            const apexMeasurementTarget = this.#getApexMeasurementTarget()
            container.addChild(apexMeasurementTarget.container)
            this.renderTargets.push(apexMeasurementTarget)

            // if (this.renderFeatures.edges) {
            //     for (const { value: edge } of this.deck.edges) {
            //         const edgeTarget = this.#getEdgeTarget(edge)
            //         container.addChild(edgeTarget.container)
            //         this.renderTargets.push(edgeTarget)
            //     }
            // }

            // if (this.renderFeatures.measurements) {
            //     for (const { value: edge } of this.deck.edges) {
            //         const measurementTarget = this.#getMeasurementTarget(edge)
            //         container.addChild(measurementTarget.container)
            //         this.renderTargets.push(measurementTarget)
            //     }
            // }

            // if (this.renderFeatures.types) {
            //     for (const { value: edge } of this.deck.edges) {
            //         const typeTarget = this.#getTypeTarget(edge)
            //         container.addChild(typeTarget.container)
            //         this.renderTargets.push(typeTarget)
            //     }
            // }

            for (const child of this.app.stage.children) {
                this.app.stage.removeChild(child)
            }
            this.app.stage.addChild(container)
        }

        // Update global offset of root Pixi container in order to keep the
        // rendered deck/content centred on the screen. Note that the scale of
        // the root Pixi container is *not* updated here such that scaling
        // issues with elements such as text (which should always be a constant
        // size) are avoided.
        const { x, y } = this.offset
        this.app.stage.children[0].x = x
        this.app.stage.children[0].y = y

        for (const { render } of this.renderTargets) { render() }
    }

    #getApexCrossSectionTarget() {
        const container = new Container()
        const graphic = new Graphics()
        container.addChild(graphic)

        const render = () => {
            if (this.deck.apexes.size === 0) { return }

            const intercept = this.deck.apexGradient === 0
                ? "yIntercept" : "xIntercept"
            const vertices = [new Point(0, 0)]
            for (const apex of this.deck.apexes) {
                vertices.push(new Point(
                    apex[intercept] * this.scale,
                    - apex.height * this.scale
                ))
            }
            vertices.push(new Point(
                (this.deck.apexGradient === 0
                    ? this.deck.boundingBox.height
                    : this.deck.boundingBox.width) * this.scale,
                0
            ))

            graphic.clear()
                .poly(vertices)
                .fill(this.colors.deckFill)
                .stroke({
                    color: hslToRgb(0, 0, 13).hex,
                    width: 2,
                    cap: "square"
                })
        }

        return { container, render }
    }

    #getApexTarget(apex) {
        const container = new Container()
        const graphic = new Graphics()
        container.addChild(graphic)

        const render = ({ width = 2 } = {}) => {
            const easeFactor = (1 + Math.sin((Date.now() * 2 * Math.PI / 1200))) / 2
            const {
                edgeHue,
                edgeSaturation,
                edgeLightness,
                edgeWidth
            } = this.selectedApex === apex
                ? {
                    edgeHue: 206,
                    edgeSaturation: 0 + 71 * easeFactor,
                    edgeLightness: 0 + 19 * easeFactor,
                    edgeWidth: 2 + 6 * easeFactor
                }
                : {
                    edgeHue: 0,
                    edgeSaturation: 0,
                    edgeLightness: 13,
                    edgeWidth: width
                }
            // container.zIndex = this.selectedEdge === edge ? 2 : 1
            const intercept = this.deck.apexGradient === 0
                ? "yIntercept" : "xIntercept"
            graphic.clear()
                .moveTo(apex[intercept] * this.scale, 0)
                .lineTo(apex[intercept] * this.scale, - apex.height * this.scale)
                .stroke({
                    color: hslToRgb(edgeHue, edgeSaturation, edgeLightness).hex,
                    width: edgeWidth,
                    cap: "round"
                })
        }

        graphic.eventMode = "static"
        graphic.cursor = "pointer"
        graphic.on("pointerup", () => {
            const existingApexEditor = document.querySelector("apex-height-editor")

            if (existingApexEditor) {
                if (this.selectedApex === apex) {
                    const lengthInput = existingApexEditor.querySelector("div.unit:not(.closed) input")
                    lengthInput.select()
                }
                else {
                    existingApexEditor.apex = apex
                    const lengthInput = existingApexEditor.querySelector("div.unit:not(.closed) input")
                    lengthInput.select()
                }
                
            }
            else {
                const popoverContainer = SteppedContainer.popoverContainer()

                this.selectedApex = apex
                const apexEditor = new ApexHeightEditor()

                popoverContainer.append(apexEditor)
                this.container.loadPopover(popoverContainer)
            }
        })

        return { container, render }
    }

    #getApexMeasurementTarget({
        edgeOffset = 16,
        endOffset = 16,
        crossBarWidth = 16
    } = {}) {
        const container = new Container()
        const line = new Graphics()
        const crossBars = new Graphics()
        const text = new Text({ style: {
            fontSize: this.sizes.measurementFont,
            fill: this.colors.measurementFont,
            // fontWeight: "lighter"
            fontWeight: "bold"
        } })
        container.addChild(line, crossBars, text)

        const strokeStyle = {
            color: this.colors.measurementStroke,
            width: this.sizes.edgeStrokeWidth
        }

        // 
        const render = () => {
            const apex = this.selectedApex
            if (!apex) { return }

            const { vertexOrientation: orientation } = this.deck
            const offsetWidthRatio = edgeOffset / this.sizes.edgeStrokeWidth
            const edgeAlignment = orientation * (offsetWidthRatio + 0.5)
            const measurementLength = apex.height * this.scale

            line.clear()
                .moveTo(endOffset, 0)
                .lineTo(measurementLength - endOffset, 0)
                .stroke({ ...strokeStyle, alignment: edgeAlignment })

            const barAlignment = orientation * edgeOffset
            const crossBarStart = crossBarWidth / 2 - barAlignment
            const crossBarEnd = - (barAlignment + crossBarWidth / 2)
            crossBars.clear()
                .moveTo(endOffset, crossBarStart)
                .lineTo(endOffset, crossBarEnd)
                .stroke(strokeStyle)
                .moveTo(measurementLength - endOffset, crossBarStart)
                .lineTo(measurementLength - endOffset, crossBarEnd)
                .stroke(strokeStyle)

            // text.style.fontWeight = this.deck.isSetEdgeLength(edge) ? "bold" : "lighter"
            if (this.flags.metric) {
                text.text = `${(apex.height / 100).toFixed(2)}m`
            }
            else {
                const { feet, inches } = metricToImperial(apex.height / 100)
                text.text = `${Math.round(feet)}' ${Math.round(inches)}"`
            }

            container.rotation = Math.PI / 2
            container.x = this.width.eaves * this.scale
            container.y = - apex.height * this.scale

            text.x = endOffset + this.sizes.measurementTextOffset
            text.y = - (edgeOffset + text.height)
        }

        return { container, render }
    }

    #getEavesMeasurementTarget({
        edgeOffset = - 16,
        endOffset = 16,
        crossBarWidth = 16
    } = {}) {
        const container = new Container()
        const line = new Graphics()
        const crossBars = new Graphics()
        const text = new Text({ style: {
            fontSize: this.sizes.measurementFont,
            fill: this.colors.measurementFont,
            // fontWeight: "lighter"
            fontWeight: "bold"
        } })
        container.addChild(line, crossBars, text)

        const strokeStyle = {
            color: this.colors.measurementStroke,
            width: this.sizes.edgeStrokeWidth
        }

        // 
        const render = () => {
            const { vertexOrientation: orientation } = this.deck
            const offsetWidthRatio = edgeOffset / this.sizes.edgeStrokeWidth
            const edgeAlignment = orientation * (offsetWidthRatio + 0.5)
            const measurementLength = this.width.eaves * this.scale

            line.clear()
                .moveTo(endOffset, 0)
                .lineTo(measurementLength - endOffset, 0)
                .stroke({ ...strokeStyle, alignment: edgeAlignment })

            const barAlignment = orientation * edgeOffset
            const crossBarStart = crossBarWidth / 2 - barAlignment
            const crossBarEnd = - (barAlignment + crossBarWidth / 2)
            crossBars.clear()
                .moveTo(endOffset, crossBarStart)
                .lineTo(endOffset, crossBarEnd)
                .stroke(strokeStyle)
                .moveTo(measurementLength - endOffset, crossBarStart)
                .lineTo(measurementLength - endOffset, crossBarEnd)
                .stroke(strokeStyle)

            // text.style.fontWeight = this.deck.isSetEdgeLength(edge) ? "bold" : "lighter"
            if (this.flags.metric) {
                text.text = `${(this.width.eaves / 100).toFixed(2)}m`
            }
            else {
                const { feet, inches } = metricToImperial(this.width.eaves / 100)
                text.text = `${Math.round(feet)}' ${Math.round(inches)}"`
            }

            text.x = measurementLength - endOffset - this.sizes.measurementTextOffset - text.width
            text.y = - edgeOffset
        }

        return { container, render }
    }

    get width() {
        return {
            eaves: this.deck.apexGradient === 0
                ? this.deck.boundingBox.height
                : this.deck.boundingBox.width,
            renderer: this.app.renderer.width
        }
    }

    get height() {
        return {
            apex: [...this.deck.apexes]
                .sort((apexA, apexB) => apexB.height - apexA.height)[0]
                .height || 0,
            renderer: this.app.renderer.height
        }
    }

    get offset() {
        const { eaves: eavesWidth, renderer: rendererWidth } = this.width
        const { apex: apexHeight, renderer: rendererHeight } = this.height

        const anchorX = (rendererWidth - eavesWidth * this.scale) / 2
        const anchorY = (rendererHeight + apexHeight * this.scale) / 2

        return new Vector2D(anchorX, anchorY)
            .add(this.offsets.combined)
    }

    get scale() {
        const { eaves: eavesWidth, renderer: rendererWidth } = this.width
        const { apex: apexHeight, renderer: rendererHeight } = this.height

        const scale = Math.min(
            rendererWidth / eavesWidth,
            (rendererHeight + this.canvasHeightCorrections.combined) / apexHeight
        )

        return scale * this.scales.combined
    }

    static get customName() {
        return "apex-renderer"
    }
}

// @@exports
export { DeckRenderer, ApexRenderer }
